/**
 * F-releve-indy F4 — parser do libellé bancário (SG/Wise) para exibição
 * "à la Indy": o que identifica a transação é QUEM (contraparte após
 * "DE:"/"POUR:") e POR QUÊ (após "MOTIF:", geralmente o nº da fatura).
 * O texto cru (nº do VIR, REF, jargão) vira detalhe expansível.
 */

/** Chaves i18n dos tipos (compartilhadas entre /banco e Tesouraria). */
export const BANK_KIND_LABEL_KEYS = {
  vir_recu: "bankStatements.lblVirRecu",
  vir_emis: "bankStatements.lblVirEmis",
  prlv: "bankStatements.lblPrlv",
  cb: "bankStatements.lblCb",
  frais: "bankStatements.lblFrais",
} as const;

/**
 * Corta o jargão que segue um nome quando o libellé NÃO usa separador "|"
 * (formato dos lançamentos da Tesouraria): "FREE MOBILE ID: FR07… REF: …"
 * → "FREE MOBILE". Marcadores: ID/REF/DATE/MANDAT/MOTIF/CPT/BQ/SG, pares
 * de dígitos "21 04" (dia mês) e referências numéricas longas.
 */
function cutTrailingNoise(s: string): string {
  let out = s;
  const marker = /\s+(?:ID|REF|DATE|MANDAT|MOTIF|CPT|BQ|SG)\s*:?\b[\s\S]*$/i.exec(out);
  if (marker) out = out.slice(0, marker.index);
  out = out.replace(/\s+\d{2}\s+\d{2}\b[\s\S]*$/, "");
  out = out.replace(/\s+\d{6,}[\s\S]*$/, "");
  return out.trim();
}

/** Fallback quando não há contraparte: 1º segmento do libellé, sem jargão. */
export function bankLabelFallback(description: string | null | undefined): string {
  const first = (description ?? "—").split(/\s*[|—]\s*/)[0]!.trim();
  return cutTrailingNoise(first) || first || "—";
}

export interface ParsedBankLabel {
  /** Código do tipo (a UI traduz): vir_recu, vir_emis, prlv, cb, frais. */
  kind: "vir_recu" | "vir_emis" | "prlv" | "cb" | "frais" | null;
  /** Contraparte extraída de DE:/POUR: (Title Case). */
  counterparty: string | null;
  /** Conteúdo do MOTIF: (mantido como veio, ex. "FACTURE F2026-00328"). */
  motif: string | null;
}

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/(^|[\s\-'.])([a-zà-ü])/g, (m, sep: string, ch: string) => sep + ch.toUpperCase())
    .trim();
}

export function parseBankLabel(description: string): ParsedBankLabel {
  const d = description ?? "";
  const upper = d.toUpperCase();

  let kind: ParsedBankLabel["kind"] = null;
  if (/\bVIR(EMENT)?\s+RECU\b|VIREMENTS\s+RECUS/.test(upper)) kind = "vir_recu";
  else if (/\bVIR(EMENT)?\b.*\bEMIS\b|\bVIREMENTS?\s+EMIS\b/.test(upper)) kind = "vir_emis";
  else if (/\bPR(E)?L(E)?V(EMENT)?\b/.test(upper)) kind = "prlv";
  else if (/^CB\b|\bCB\s/.test(upper)) kind = "cb";
  else if (/\bFRAIS\b|\bCOTISATION\b|\bCOMMISSION\b/.test(upper)) kind = "frais";

  // Contraparte: "POUR CPTE DE:" > "DE:" > "POUR:". Corta no próximo separador.
  let counterparty: string | null = null;
  const cpMatch =
    /POUR\s+CPTE\s+DE\s*:\s*([^|]+)/i.exec(d) ??
    /(?:^|\||\s)DE\s*:\s*([^|]+)/i.exec(d) ??
    /POUR\s*:\s*([^|]+)/i.exec(d);
  if (cpMatch?.[1]) {
    const cleaned = cutTrailingNoise(cpMatch[1].replace(/\s+/g, " ").trim());
    counterparty = cleaned.length > 0 ? titleCase(cleaned) : null;
  }

  let motif: string | null = null;
  const mMatch = /MOTIF\s*:\s*([^|]+)/i.exec(d);
  if (mMatch?.[1]) {
    motif = mMatch[1].replace(/\s+/g, " ").trim();
    if (motif.length === 0) motif = null;
  }

  return { kind, counterparty, motif };
}
